import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  MessageSquare,
  Mail,
  Smartphone,
  Settings,
  Save,
  Send,
  Bell,
  CheckCircle,
  XCircle,
  AlertCircle,
  ExternalLink,
  Eye,
  EyeOff,
  TestTube
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// SMS Provider configurations
const SMS_PROVIDERS = {
  fast2sms: {
    name: 'Fast2SMS',
    description: 'Affordable SMS for India (₹0.10/SMS)',
    fields: [
      { key: 'api_key', label: 'API Key', type: 'password', required: true }
    ],
    signupUrl: 'https://www.fast2sms.com/',
    country: 'India'
  },
  twilio_sms: {
    name: 'Twilio SMS',
    description: 'Global SMS provider',
    fields: [
      { key: 'account_sid', label: 'Account SID', type: 'text', required: true },
      { key: 'auth_token', label: 'Auth Token', type: 'password', required: true },
      { key: 'phone_number', label: 'Twilio Phone Number', type: 'text', required: true, placeholder: '+1234567890' }
    ],
    signupUrl: 'https://www.twilio.com/console',
    country: 'Global'
  },
  msg91: {
    name: 'MSG91',
    description: 'Popular SMS provider for India',
    fields: [
      { key: 'api_key', label: 'Auth Key', type: 'password', required: true },
      { key: 'sender_id', label: 'Sender ID', type: 'text', required: true, placeholder: 'SCHOOL' }
    ],
    signupUrl: 'https://msg91.com/',
    country: 'India'
  },
  textlocal: {
    name: 'TextLocal',
    description: 'SMS provider for India & UK',
    fields: [
      { key: 'api_key', label: 'API Key', type: 'password', required: true },
      { key: 'sender', label: 'Sender ID', type: 'text', required: true, placeholder: 'TXTLCL' }
    ],
    signupUrl: 'https://www.textlocal.in/',
    country: 'India, UK'
  }
};

// WhatsApp Provider configurations
const WHATSAPP_PROVIDERS = {
  twilio_whatsapp: {
    name: 'Twilio WhatsApp',
    description: 'Official WhatsApp Business API via Twilio',
    fields: [
      { key: 'account_sid', label: 'Account SID', type: 'text', required: true },
      { key: 'auth_token', label: 'Auth Token', type: 'password', required: true },
      { key: 'whatsapp_number', label: 'WhatsApp Number', type: 'text', required: true, placeholder: '+14155238886' }
    ],
    signupUrl: 'https://www.twilio.com/whatsapp',
    country: 'Global'
  },
  gupshup: {
    name: 'Gupshup',
    description: 'WhatsApp Business API for India',
    fields: [
      { key: 'api_key', label: 'API Key', type: 'password', required: true },
      { key: 'app_name', label: 'App Name', type: 'text', required: true },
      { key: 'source_phone', label: 'Source Phone', type: 'text', required: true, placeholder: '919876543210' }
    ],
    signupUrl: 'https://www.gupshup.io/',
    country: 'India'
  },
  wati: {
    name: 'WATI',
    description: 'Easy WhatsApp Business Solution',
    fields: [
      { key: 'api_token', label: 'API Token', type: 'password', required: true },
      { key: 'api_url', label: 'API URL', type: 'text', required: true, placeholder: 'https://live-server-xxxx.wati.io' }
    ],
    signupUrl: 'https://www.wati.io/',
    country: 'Global'
  }
};

const NotificationSettingsPage = () => {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPasswords, setShowPasswords] = useState({});
  
  const [settings, setSettings] = useState({
    sms_enabled: false,
    whatsapp_enabled: false,
    email_enabled: true,
    auto_attendance_alert: true,
    auto_fee_reminder: true,
    auto_exam_result: true,
    sms_config: null,
    whatsapp_config: null
  });
  
  const [smsProvider, setSmsProvider] = useState('fast2sms');
  const [smsCredentials, setSmsCredentials] = useState({});
  const [whatsappProvider, setWhatsappProvider] = useState('twilio_whatsapp');
  const [whatsappCredentials, setWhatsappCredentials] = useState({});
  const [testPhone, setTestPhone] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/notification-system/settings`, {
        headers: getAuthHeader()
      });
      
      const data = response.data;
      setSettings({
        sms_enabled: data.sms_enabled || false,
        whatsapp_enabled: data.whatsapp_enabled || false,
        email_enabled: data.email_enabled !== false,
        auto_attendance_alert: data.auto_attendance_alert !== false,
        auto_fee_reminder: data.auto_fee_reminder !== false,
        auto_exam_result: data.auto_exam_result !== false,
        sms_config: data.sms_config || null,
        whatsapp_config: data.whatsapp_config || null
      });
      
      // Set provider from saved config
      if (data.sms_config?.provider) {
        setSmsProvider(data.sms_config.provider);
      }
      if (data.whatsapp_config?.provider) {
        setWhatsappProvider(data.whatsapp_config.provider);
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const payload = {
        sms_enabled: settings.sms_enabled,
        whatsapp_enabled: settings.whatsapp_enabled,
        email_enabled: settings.email_enabled,
        auto_attendance_alert: settings.auto_attendance_alert,
        auto_fee_reminder: settings.auto_fee_reminder,
        auto_exam_result: settings.auto_exam_result
      };
      
      // Add SMS config if enabled and credentials provided
      if (settings.sms_enabled && Object.keys(smsCredentials).length > 0) {
        payload.sms_config = {
          provider: smsProvider,
          enabled: true,
          ...smsCredentials
        };
      }
      
      // Add WhatsApp config if enabled and credentials provided
      if (settings.whatsapp_enabled && Object.keys(whatsappCredentials).length > 0) {
        payload.whatsapp_config = {
          provider: whatsappProvider,
          enabled: true,
          ...whatsappCredentials
        };
      }
      
      await axios.post(`${API_URL}/api/notification-system/settings`, payload, {
        headers: getAuthHeader()
      });
      
      toast.success('Notification settings saved successfully!');
      fetchSettings(); // Refresh to get updated config
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSMS = async () => {
    if (!testPhone) {
      toast.error('Please enter a test phone number');
      return;
    }
    
    // First save the current settings
    await handleSaveSettings();
    
    setTesting(true);
    try {
      // Send test notification
      const response = await axios.post(
        `${API_URL}/api/notification-system/send`,
        {
          title: 'Test SMS',
          message: 'This is a test SMS from Cloud Campus School ERP. If you received this, SMS is configured correctly!',
          category: 'general',
          priority: 'medium',
          recipient_ids: [],
          channels: ['sms']
        },
        { headers: getAuthHeader() }
      );
      
      toast.success('Test SMS sent! Check your phone.');
    } catch (error) {
      toast.error('Failed to send test SMS: ' + (error.response?.data?.detail || error.message));
    } finally {
      setTesting(false);
    }
  };

  const togglePasswordVisibility = (key) => {
    setShowPasswords(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const renderProviderFields = (providers, selectedProvider, credentials, setCredentials) => {
    const provider = providers[selectedProvider];
    if (!provider) return null;
    
    return (
      <div className="space-y-4 mt-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-medium">{provider.name}</h4>
            <p className="text-sm text-muted-foreground">{provider.description}</p>
          </div>
          <Badge variant="outline">{provider.country}</Badge>
        </div>
        
        {provider.fields.map(field => (
          <div key={field.key} className="space-y-2">
            <Label htmlFor={field.key}>
              {field.label} {field.required && <span className="text-red-500">*</span>}
            </Label>
            <div className="relative">
              <Input
                id={field.key}
                type={field.type === 'password' && !showPasswords[field.key] ? 'password' : 'text'}
                placeholder={field.placeholder || `Enter ${field.label}`}
                value={credentials[field.key] || ''}
                onChange={(e) => setCredentials(prev => ({ ...prev, [field.key]: e.target.value }))}
                className="pr-10"
              />
              {field.type === 'password' && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => togglePasswordVisibility(field.key)}
                >
                  {showPasswords[field.key] ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              )}
            </div>
          </div>
        ))}
        
        <div className="pt-2">
          <a
            href={provider.signupUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-primary hover:underline flex items-center gap-1"
          >
            Get {provider.name} API Keys <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardLayout title="Notification Settings">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Notification Settings">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Bell className="h-6 w-6" /> Notification Settings
            </h1>
            <p className="text-muted-foreground">Configure SMS, WhatsApp, and Email notifications</p>
          </div>
          <Button onClick={handleSaveSettings} disabled={saving} data-testid="save-notification-settings-btn">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>

        {/* Status Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className={settings.sms_enabled ? 'border-green-500/50' : ''}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-full ${settings.sms_enabled ? 'bg-green-100 text-green-600' : 'bg-muted text-muted-foreground'}`}>
                    <Smartphone className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-medium">SMS</p>
                    <p className="text-sm text-muted-foreground">
                      {settings.sms_enabled ? (
                        <span className="text-green-600 flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" /> Enabled
                        </span>
                      ) : (
                        <span className="flex items-center gap-1">
                          <XCircle className="h-3 w-3" /> Disabled
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={settings.sms_enabled}
                  onCheckedChange={(checked) => setSettings({ ...settings, sms_enabled: checked })}
                  data-testid="sms-toggle"
                />
              </div>
            </CardContent>
          </Card>

          <Card className={settings.whatsapp_enabled ? 'border-green-500/50' : ''}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-full ${settings.whatsapp_enabled ? 'bg-green-100 text-green-600' : 'bg-muted text-muted-foreground'}`}>
                    <MessageSquare className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-medium">WhatsApp</p>
                    <p className="text-sm text-muted-foreground">
                      {settings.whatsapp_enabled ? (
                        <span className="text-green-600 flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" /> Enabled
                        </span>
                      ) : (
                        <span className="flex items-center gap-1">
                          <XCircle className="h-3 w-3" /> Disabled
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={settings.whatsapp_enabled}
                  onCheckedChange={(checked) => setSettings({ ...settings, whatsapp_enabled: checked })}
                  data-testid="whatsapp-toggle"
                />
              </div>
            </CardContent>
          </Card>

          <Card className={settings.email_enabled ? 'border-green-500/50' : ''}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-full ${settings.email_enabled ? 'bg-green-100 text-green-600' : 'bg-muted text-muted-foreground'}`}>
                    <Mail className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-medium">Email</p>
                    <p className="text-sm text-muted-foreground">
                      {settings.email_enabled ? (
                        <span className="text-green-600 flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" /> Enabled
                        </span>
                      ) : (
                        <span className="flex items-center gap-1">
                          <XCircle className="h-3 w-3" /> Disabled
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={settings.email_enabled}
                  onCheckedChange={(checked) => setSettings({ ...settings, email_enabled: checked })}
                  data-testid="email-toggle"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Provider Configuration Tabs */}
        <Tabs defaultValue="sms" className="space-y-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="sms" className="flex items-center gap-2">
              <Smartphone className="h-4 w-4" /> SMS
            </TabsTrigger>
            <TabsTrigger value="whatsapp" className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" /> WhatsApp
            </TabsTrigger>
            <TabsTrigger value="auto" className="flex items-center gap-2">
              <Settings className="h-4 w-4" /> Auto Triggers
            </TabsTrigger>
          </TabsList>

          {/* SMS Tab */}
          <TabsContent value="sms">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Smartphone className="h-5 w-5" /> SMS Configuration
                </CardTitle>
                <CardDescription>
                  Configure your SMS provider to send notifications to parents and students
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {!settings.sms_enabled ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <AlertCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>SMS is currently disabled. Enable it above to configure.</p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label>Select SMS Provider</Label>
                      <Select value={smsProvider} onValueChange={setSmsProvider}>
                        <SelectTrigger data-testid="sms-provider-select">
                          <SelectValue placeholder="Select provider" />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(SMS_PROVIDERS).map(([key, provider]) => (
                            <SelectItem key={key} value={key}>
                              <span className="flex items-center gap-2">
                                {provider.name}
                                <Badge variant="outline" className="text-xs">{provider.country}</Badge>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {renderProviderFields(SMS_PROVIDERS, smsProvider, smsCredentials, setSmsCredentials)}

                    {/* Test SMS */}
                    <div className="border-t pt-6 mt-6">
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <TestTube className="h-4 w-4" /> Test SMS
                      </h4>
                      <div className="flex gap-3">
                        <Input
                          placeholder="Enter phone number (e.g., 9876543210)"
                          value={testPhone}
                          onChange={(e) => setTestPhone(e.target.value)}
                          className="flex-1"
                          data-testid="test-phone-input"
                        />
                        <Button 
                          onClick={handleTestSMS} 
                          disabled={testing || !testPhone}
                          variant="outline"
                          data-testid="send-test-sms-btn"
                        >
                          <Send className="h-4 w-4 mr-2" />
                          {testing ? 'Sending...' : 'Send Test'}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        Save settings first, then send a test SMS to verify configuration
                      </p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* WhatsApp Tab */}
          <TabsContent value="whatsapp">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" /> WhatsApp Configuration
                </CardTitle>
                <CardDescription>
                  Configure WhatsApp Business API to send notifications
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {!settings.whatsapp_enabled ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <AlertCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>WhatsApp is currently disabled. Enable it above to configure.</p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label>Select WhatsApp Provider</Label>
                      <Select value={whatsappProvider} onValueChange={setWhatsappProvider}>
                        <SelectTrigger data-testid="whatsapp-provider-select">
                          <SelectValue placeholder="Select provider" />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(WHATSAPP_PROVIDERS).map(([key, provider]) => (
                            <SelectItem key={key} value={key}>
                              <span className="flex items-center gap-2">
                                {provider.name}
                                <Badge variant="outline" className="text-xs">{provider.country}</Badge>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {renderProviderFields(WHATSAPP_PROVIDERS, whatsappProvider, whatsappCredentials, setWhatsappCredentials)}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Auto Triggers Tab */}
          <TabsContent value="auto">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" /> Automatic Notification Triggers
                </CardTitle>
                <CardDescription>
                  Configure which events should automatically send notifications
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between py-3 border-b">
                  <div className="space-y-0.5">
                    <Label className="text-base">Attendance Alerts</Label>
                    <p className="text-sm text-muted-foreground">
                      Send notification when student is marked absent or late
                    </p>
                  </div>
                  <Switch
                    checked={settings.auto_attendance_alert}
                    onCheckedChange={(checked) => setSettings({ ...settings, auto_attendance_alert: checked })}
                    data-testid="auto-attendance-toggle"
                  />
                </div>

                <div className="flex items-center justify-between py-3 border-b">
                  <div className="space-y-0.5">
                    <Label className="text-base">Fee Reminders</Label>
                    <p className="text-sm text-muted-foreground">
                      Send reminders for upcoming and overdue fee payments
                    </p>
                  </div>
                  <Switch
                    checked={settings.auto_fee_reminder}
                    onCheckedChange={(checked) => setSettings({ ...settings, auto_fee_reminder: checked })}
                    data-testid="auto-fee-toggle"
                  />
                </div>

                <div className="flex items-center justify-between py-3">
                  <div className="space-y-0.5">
                    <Label className="text-base">Exam Results</Label>
                    <p className="text-sm text-muted-foreground">
                      Notify parents when exam results are published
                    </p>
                  </div>
                  <Switch
                    checked={settings.auto_exam_result}
                    onCheckedChange={(checked) => setSettings({ ...settings, auto_exam_result: checked })}
                    data-testid="auto-exam-toggle"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Help Section */}
        <Card className="bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
          <CardContent className="pt-6">
            <div className="flex gap-4">
              <AlertCircle className="h-6 w-6 text-blue-600 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-blue-900 dark:text-blue-100">Provider Recommendations</h4>
                <ul className="mt-2 text-sm text-blue-700 dark:text-blue-300 space-y-1">
                  <li>• <strong>For India:</strong> Fast2SMS (cheapest at ₹0.10/SMS) or MSG91</li>
                  <li>• <strong>For Global:</strong> Twilio (reliable but more expensive)</li>
                  <li>• <strong>For WhatsApp:</strong> Gupshup (India) or Twilio WhatsApp (Global)</li>
                  <li>• SMS notifications are sent to parent/student phone numbers from their profiles</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button at bottom */}
        <div className="flex justify-end">
          <Button onClick={handleSaveSettings} disabled={saving} size="lg" data-testid="save-settings-bottom-btn">
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save All Settings'}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default NotificationSettingsPage;
